import { useState } from 'react';

type FormState = {
  name: string;
  email: string;
  phone: string;
  business: string;
  message: string;
};

export function HomepageContactForm() {
  const [formData, setFormData] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    business: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const validate = () => {
    const nextErrors: Partial<Record<keyof FormState, string>> = {};

    if (!formData.name.trim()) nextErrors.name = 'Your name is required';
    if (!formData.email.trim()) nextErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) nextErrors.email = 'Enter a valid email';
    if (!formData.phone.trim()) nextErrors.phone = 'Phone number is required';
    if (!formData.business.trim()) nextErrors.business = 'Business name is required';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const key = name as keyof FormState;

    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('business', formData.business);
      body.append('message', formData.message);
      body.append('source', 'homepage-contact-form');

      try {
        await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        });
      } catch {
        // Local fallback: keep the form experience stable even without a backend.
      }

      setSubmitted(true);
    } catch {
      setErrors({ message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-3xl bg-white shadow-[0_20px_60px_rgba(0,191,165,0.18)] border border-[#00BFA5]/40 p-8 lg:p-10 text-center">
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-primary/10 flex items-center justify-center">
          <i className="ri-check-line text-primary text-3xl" />
        </div>
        <h3 className="font-display text-2xl font-bold text-secondary mb-3">Thanks — we got it.</h3>
        <p className="text-gray-600 leading-relaxed max-w-lg mx-auto">
          We will review your message and reach out with the next best step.
        </p>
      </div>
    );
  }

  return (
    <section id="contact-form" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
          <div className="space-y-6">
            <span className="inline-block bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full">
              Prefer to send a quick message?
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-secondary leading-tight">
              Tell us what you need and we’ll take it from there.
            </h2>
            <p className="text-xl text-gray-600 font-light leading-relaxed max-w-2xl">
              If you are not ready to book a call yet, send a few details and we’ll follow up with a simple recommendation.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
              {[
                'Missed call recovery',
                '24/7 lead capture',
                'Appointment booking',
                'Simple setup, no pressure',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-2xl bg-gray-50 px-4 py-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-primary text-sm" />
                  </div>
                  <span className="text-sm font-medium text-secondary">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl bg-gradient-to-br from-[#00BFA5] to-[#00BFA5]/80 p-1 shadow-[0_0_0_1px_rgba(0,191,165,0.18),0_20px_60px_rgba(0,191,165,0.16)]">
            <div className="rounded-[22px] bg-white p-6 lg:p-8">
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Your Name *</label>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Jane Doe"
                      className={`w-full px-4 py-3 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#00BFA5]/20 focus:border-[#00BFA5] ${
                        errors.name ? 'border-red-400' : 'border-gray-200'
                      }`}
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Business Name *</label>
                    <input
                      name="business"
                      value={formData.business}
                      onChange={handleChange}
                      placeholder="Your Business"
                      className={`w-full px-4 py-3 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#00BFA5]/20 focus:border-[#00BFA5] ${
                        errors.business ? 'border-red-400' : 'border-gray-200'
                      }`}
                    />
                    {errors.business && <p className="text-red-500 text-xs mt-1">{errors.business}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="jane@company.com"
                      className={`w-full px-4 py-3 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#00BFA5]/20 focus:border-[#00BFA5] ${
                        errors.email ? 'border-red-400' : 'border-gray-200'
                      }`}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className={`w-full px-4 py-3 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#00BFA5]/20 focus:border-[#00BFA5] ${
                        errors.phone ? 'border-red-400' : 'border-gray-200'
                      }`}
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">What would you like help with?</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us about missed calls, slow lead response, booking issues, or anything else we should know."
                    className={`w-full px-4 py-3 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none ${
                      errors.message ? 'border-red-400' : 'border-gray-200'
                    }`}
                  />
                  {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-full font-semibold text-base transition-all duration-200 hover:scale-[1.01] hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send My Details
                      <i className="ri-arrow-right-line text-lg" />
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-gray-400 leading-relaxed">
                  We will never spam you. This is just a quick way to start the conversation.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
